// Worker 対応の試験のメイン側 (#52 ① 段階 0)。Worker を 1 つ起こして結果を受け取るだけ。
//
// **通話とは別の接続**を張るので、通話中には使わない (画面のボタンから手で動かす)。
// 入室しないため部屋には影響しない。

import ProbeWorker from "./worker-probe-worker.ts?worker";
import type { ProbeRequest, ProbeResult } from "./probe-messages.ts";

/** 1 段ごとの待ち時間の上限 (ms)。モバイル回線でのハンドシェイクを考えて少し長め。 */
const STEP_TIMEOUT_MS = 8_000;

/**
 * **案 A（WebTransport ごと Worker に移す）がこの端末で成立するか。**
 *
 * 段階 1 以降が必要とするのは「接続 + Datagram の両方向 + 双方向ストリーム」の全部なので、
 * **1 つでも欠けたら成立しない**。
 */
export function isProbeOk(r: ProbeResult): boolean {
  return (
    r.nameAvailable &&
    r.connected === true &&
    r.datagramsReadable === true &&
    r.datagramsWritable !== null &&
    r.bidirectionalStream === true &&
    r.roomsReceived !== null &&
    r.error === null
  );
}

/**
 * **どの段で止まったか**を日本語で返す。全部通れば `null`。
 *
 * 「止まった段」が分かれば、次に何を調べればよいかが決まる（名前が無いなら Safari の
 * 対応待ち、接続が通らないなら証明書か経路、ストリームだけ駄目なら案 A の設計変更）。
 */
export function probeStoppedAt(r: ProbeResult): string | null {
  if (!r.nameAvailable) return "① Worker に WebTransport が無い";
  if (r.connected !== true) return "② 接続（QUIC ハンドシェイク）";
  if (r.datagramsReadable !== true) return "③ Datagram の受信口";
  if (r.datagramsWritable === null) return "③ Datagram の送信口";
  if (r.bidirectionalStream !== true) return "④ 双方向ストリームを開く";
  if (r.roomsReceived === null) return "④ list_rooms の往復";
  return null;
}

/**
 * 結果をログの行に直す。**純粋関数**にしてあるので試験できる（Worker も WebTransport も
 * vitest の node 環境には無い）。
 */
export function formatProbeResult(r: ProbeResult): string[] {
  const has = (ok: boolean | null) => (ok === null ? "—" : ok ? "あり" : "無し");
  const detail =
    `Worker 試験: WebTransport=${has(r.nameAvailable)}` +
    ` / 接続=${r.connected === null ? "—" : r.connected ? "成功" : "失敗"}` +
    ` / Datagram 受信口=${has(r.datagramsReadable)}` +
    ` / 送信口=${r.datagramsWritable ?? "無し"}` +
    ` / 双方向ストリーム=${r.bidirectionalStream === null ? "—" : r.bidirectionalStream ? "成功" : "失敗"}` +
    ` / 部屋=${r.roomsReceived === null ? "—" : `${r.roomsReceived} 件`}` +
    ` / ${r.elapsedMs} ms`;
  const stopped = probeStoppedAt(r);
  const head = isProbeOk(r)
    ? "Worker 試験: ○ この端末では案 A（WebTransport を Worker に移す）が成立する"
    : `Worker 試験: × 案 A は成立しない — 止まった段: ${stopped ?? "不明"}`;
  const lines = [head, detail];
  if (r.error !== null) lines.push(`Worker 試験: 理由 ${r.error}`);
  return lines;
}

/**
 * Worker を起こして試験する。**Worker は結果を受け取ったら必ず畳む**
 * （残すと通話とは別の接続を抱えたままになる）。
 */
export async function probeWorkerTransport(
  url: string,
  stepTimeoutMs: number = STEP_TIMEOUT_MS,
): Promise<ProbeResult> {
  const empty = (error: string): ProbeResult => ({
    nameAvailable: false,
    connected: null,
    datagramsReadable: null,
    datagramsWritable: null,
    bidirectionalStream: null,
    roomsReceived: null,
    error,
    elapsedMs: 0,
  });
  if (typeof Worker === "undefined") return empty("このブラウザに Worker が無い");

  let worker: Worker;
  try {
    worker = new ProbeWorker();
  } catch (e) {
    return empty(`Worker を作れない: ${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`);
  }
  try {
    return await new Promise<ProbeResult>((resolve) => {
      // 外側の上限は段の数ぶん + 余白。Worker が黙ったままでも必ず返る
      const timer = setTimeout(
        () => resolve(empty(`Worker が ${stepTimeoutMs * 4 + 2_000} ms 返事をしない`)),
        stepTimeoutMs * 4 + 2_000,
      );
      worker.addEventListener("message", (e: MessageEvent<ProbeResult>) => {
        clearTimeout(timer);
        resolve(e.data);
      });
      worker.addEventListener("error", (e: ErrorEvent) => {
        clearTimeout(timer);
        resolve(empty(`Worker が落ちた: ${e.message || "内容不明"}`));
      });
      const request: ProbeRequest = { url, timeoutMs: stepTimeoutMs };
      worker.postMessage(request);
    });
  } finally {
    worker.terminate();
  }
}
